import type { Store } from '@blocksuite/store';

import {
  getCrossDocCellValue,
  isCrossDocRelationServiceInitialized,
  updateCrossDocCellValue,
} from './database-discovery-bridge';
import { getDatabaseDataSource } from './row-projection';

/**
 * Kontext fuer den bidirektionalen Sync
 */
export interface SyncContext {
  store: Store;
  sourceDatabaseId: string; // Database we're editing from
  sourcePropertyId: string; // Property we're editing (relation column in source DB)
  sourceRowId: string;
  targetDatabaseId: string;
  targetDocId?: string; // For cross-doc relations
  inverseFieldId: string;
  inverseCardinality: 'one' | 'many';
}

/**
 * Info ueber einen erforderlichen Steal
 */
export interface StealInfo {
  currentOwnerId: string;
  currentOwnerDisplay: string;
  targetRowId: string;
  targetRowDisplay: string;
}

/**
 * Prueft ob ein Steal erforderlich ist (bei inverseCardinality='one')
 * Wenn das Ziel bereits einen anderen Owner hat, muss der User bestaetigen
 */
export async function checkStealRequired(
  ctx: SyncContext,
  targetRowId: string
): Promise<StealInfo | null> {
  // Steal nur bei 'one' Cardinality
  if (ctx.inverseCardinality !== 'one') {
    return null;
  }

  // Check if this is a cross-doc relation
  const currentDocId = ctx.store.id;
  const isCrossDoc =
    ctx.targetDocId &&
    ctx.targetDocId !== currentDocId &&
    ctx.targetDocId.length > 0;

  let currentArray: string[];

  if (isCrossDoc && isCrossDocRelationServiceInitialized()) {
    // Cross-doc: use bridge to get cell value
    const result = await getCrossDocCellValue(
      ctx.targetDocId!,
      ctx.targetDatabaseId,
      targetRowId,
      ctx.inverseFieldId
    );
    currentArray = result.success ? (result.value ?? []) : [];
  } else {
    // Local: use local store
    const targetDataSource = getDatabaseDataSource(
      ctx.store,
      ctx.targetDatabaseId
    );
    if (!targetDataSource) {
      return null;
    }

    const currentValue = targetDataSource.cellValueGet(
      targetRowId,
      ctx.inverseFieldId
    );
    currentArray = normalizeToArray(currentValue);
  }

  // Kein Owner -> kein Steal
  if (currentArray.length === 0) {
    return null;
  }

  const currentOwnerId = currentArray[0];

  // Bereits der Owner -> kein Steal
  if (currentOwnerId === ctx.sourceRowId) {
    return null;
  }

  // Steal erforderlich
  // Note: currentOwnerId is a row in the SOURCE database (where we're editing)
  // targetRowId is a row in the TARGET database (where relation points to)
  return {
    currentOwnerId,
    currentOwnerDisplay: getRowDisplay(
      ctx.store,
      ctx.sourceDatabaseId, // currentOwner is in source DB
      currentOwnerId
    ),
    targetRowId,
    targetRowDisplay: getRowDisplay(
      ctx.store,
      ctx.targetDatabaseId, // target is in target DB
      targetRowId
    ),
  };
}

/**
 * Fuehrt den bidirektionalen Sync durch
 * @param action 'add' oder 'remove'
 */
export async function syncInverseRelation(
  ctx: SyncContext,
  targetRowId: string,
  action: 'add' | 'remove'
): Promise<string[]> {
  // Check if this is a cross-doc relation
  const currentDocId = ctx.store.id;
  const isCrossDoc =
    ctx.targetDocId &&
    ctx.targetDocId !== currentDocId &&
    ctx.targetDocId.length > 0;

  if (isCrossDoc && isCrossDocRelationServiceInitialized()) {
    // Cross-doc: use bridge
    console.log('[sync] Cross-doc sync:', {
      targetDocId: ctx.targetDocId,
      targetDatabaseId: ctx.targetDatabaseId,
      targetRowId,
      inverseFieldId: ctx.inverseFieldId,
      action,
    });

    // Get current value from cross-doc
    const getResult = await getCrossDocCellValue(
      ctx.targetDocId!,
      ctx.targetDatabaseId,
      targetRowId,
      ctx.inverseFieldId
    );
    if (!getResult.success) {
      throw (
        getResult.error ?? new Error('Failed to read cross-doc relation cell')
      );
    }
    const currentArray = getResult.success ? (getResult.value ?? []) : [];

    // Calculate new value
    let newValue: string[];
    if (action === 'add') {
      if (ctx.inverseCardinality === 'one') {
        newValue = [ctx.sourceRowId];
      } else {
        newValue = currentArray.includes(ctx.sourceRowId)
          ? currentArray
          : [...currentArray, ctx.sourceRowId];
      }
    } else {
      newValue = currentArray.filter(id => id !== ctx.sourceRowId);
    }

    // Update cross-doc cell value
    const updateResult = await updateCrossDocCellValue(
      ctx.targetDocId!,
      ctx.targetDatabaseId,
      targetRowId,
      ctx.inverseFieldId,
      newValue
    );

    if (!updateResult.success) {
      throw (
        updateResult.error ??
        new Error('Failed to update cross-doc relation cell')
      );
    }
    return currentArray;
  }

  // Local: use local store
  const targetDataSource = getDatabaseDataSource(
    ctx.store,
    ctx.targetDatabaseId
  );
  if (!targetDataSource) {
    throw new Error(`Target database not found: ${ctx.targetDatabaseId}`);
  }

  // Aktuellen Wert holen
  const currentValue = targetDataSource.cellValueGet(
    targetRowId,
    ctx.inverseFieldId
  );
  const currentArray = normalizeToArray(currentValue);

  let newValue: string[];

  if (action === 'add') {
    if (ctx.inverseCardinality === 'one') {
      // Replace (Steal wurde bereits bestaetigt)
      newValue = [ctx.sourceRowId];
    } else {
      // Append (nur wenn noch nicht vorhanden)
      newValue = currentArray.includes(ctx.sourceRowId)
        ? currentArray
        : [...currentArray, ctx.sourceRowId];
    }
  } else {
    // Remove
    newValue = currentArray.filter(id => id !== ctx.sourceRowId);
  }

  // Wert setzen
  targetDataSource.cellValueChange(targetRowId, ctx.inverseFieldId, newValue);
  return currentArray;
}

export async function restoreInverseRelationValue(
  ctx: SyncContext,
  targetRowId: string,
  value: string[]
): Promise<void> {
  const currentDocId = ctx.store.id;
  const isCrossDoc =
    ctx.targetDocId &&
    ctx.targetDocId !== currentDocId &&
    ctx.targetDocId.length > 0;

  if (isCrossDoc && isCrossDocRelationServiceInitialized()) {
    const result = await updateCrossDocCellValue(
      ctx.targetDocId!,
      ctx.targetDatabaseId,
      targetRowId,
      ctx.inverseFieldId,
      value
    );
    if (!result.success) {
      throw (
        result.error ?? new Error('Failed to rollback cross-doc relation cell')
      );
    }
    return;
  }

  const targetDataSource = getDatabaseDataSource(
    ctx.store,
    ctx.targetDatabaseId
  );
  if (!targetDataSource) {
    throw new Error(`Target database not found: ${ctx.targetDatabaseId}`);
  }
  targetDataSource.cellValueChange(targetRowId, ctx.inverseFieldId, value);
}

/**
 * Entfernt die Target-Row vom vorherigen Owner in der Source-Database (bei Steal)
 * Der vorherige Owner ist eine andere Row in der Source-DB die bereits auf targetRowId zeigt
 */
export function removeFromPreviousOwner(
  ctx: SyncContext,
  previousOwnerId: string,
  targetRowId: string
): string[] {
  const sourceDataSource = getDatabaseDataSource(
    ctx.store,
    ctx.sourceDatabaseId
  );
  if (!sourceDataSource) {
    throw new Error(`Source database not found: ${ctx.sourceDatabaseId}`);
  }

  // Vom vorherigen Owner entfernen (using sourcePropertyId, not inverseFieldId!)
  const prevOwnerValue = sourceDataSource.cellValueGet(
    previousOwnerId,
    ctx.sourcePropertyId // <-- This is the relation column in source DB
  );
  const prevOwnerArray = normalizeToArray(prevOwnerValue);
  const newValue = prevOwnerArray.filter(id => id !== targetRowId);

  sourceDataSource.cellValueChange(
    previousOwnerId,
    ctx.sourcePropertyId, // <-- Use source property, not inverse field
    newValue
  );
  return prevOwnerArray;
}

function restoreSourceRelationValue(
  ctx: SyncContext,
  rowId: string,
  value: string[]
): void {
  const sourceDataSource = getDatabaseDataSource(
    ctx.store,
    ctx.sourceDatabaseId
  );
  if (!sourceDataSource) {
    throw new Error(`Source database not found: ${ctx.sourceDatabaseId}`);
  }
  sourceDataSource.cellValueChange(rowId, ctx.sourcePropertyId, value);
}

/**
 * Verarbeitet Aenderungen an einer Relation-Zelle
 * Synchronisiert automatisch das inverse Feld wenn konfiguriert
 *
 * @param targetDocId - Optional target doc ID for cross-doc relations
 */
export async function processRelationChange(
  store: Store,
  sourceDatabaseId: string,
  sourcePropertyId: string,
  sourceRowId: string,
  targetDatabaseId: string,
  inverseFieldId: string | undefined,
  inverseCardinality: 'one' | 'many',
  oldValue: string[],
  newValue: string[],
  showStealDialog: (info: StealInfo) => Promise<boolean>,
  targetDocId?: string
): Promise<string[]> {
  if (!inverseFieldId) {
    throw new Error(
      'Cannot update relation cell without an inverse relation field.'
    );
  }

  const ctx: SyncContext = {
    store,
    sourceDatabaseId,
    sourcePropertyId,
    sourceRowId,
    targetDatabaseId,
    targetDocId,
    inverseFieldId,
    inverseCardinality,
  };

  const finalValue: string[] = [];
  const rollbackTasks: Array<() => Promise<void> | void> = [];

  try {
    // Added rows
    const added = newValue.filter(id => !oldValue.includes(id));
    for (const rowId of added) {
      const stealInfo = await checkStealRequired(ctx, rowId);

      if (stealInfo) {
        // Steal erforderlich -> Dialog zeigen
        const confirmed = await showStealDialog(stealInfo);
        if (!confirmed) {
          // User hat abgebrochen -> diese Row nicht hinzufuegen
          continue;
        }
      }

      // Inverse Relation hinzufuegen
      const previousInverseValue = await syncInverseRelation(ctx, rowId, 'add');
      rollbackTasks.push(() =>
        restoreInverseRelationValue(ctx, rowId, previousInverseValue)
      );
      if (stealInfo) {
        // Only mutate the previous source owner after the inverse side was
        // updated. If cross-doc sync fails, the source document remains unchanged.
        const previousSourceValue = removeFromPreviousOwner(
          ctx,
          stealInfo.currentOwnerId,
          rowId
        );
        rollbackTasks.push(() =>
          restoreSourceRelationValue(
            ctx,
            stealInfo.currentOwnerId,
            previousSourceValue
          )
        );
      }
      finalValue.push(rowId);
    }

    // Removed rows
    const removed = oldValue.filter(id => !newValue.includes(id));
    for (const rowId of removed) {
      const previousInverseValue = await syncInverseRelation(
        ctx,
        rowId,
        'remove'
      );
      rollbackTasks.push(() =>
        restoreInverseRelationValue(ctx, rowId, previousInverseValue)
      );
    }

    // Behalte nicht-geaenderte Rows
    const unchanged = newValue.filter(id => oldValue.includes(id));
    finalValue.push(...unchanged);

    return finalValue;
  } catch (error) {
    for (const rollback of rollbackTasks.reverse()) {
      try {
        await rollback();
      } catch (rollbackError) {
        console.error(
          '[sync] Failed to rollback relation change:',
          rollbackError
        );
      }
    }
    throw error;
  }
}

/**
 * Cleanup references to deleted rows in a relation column.
 * Called when we need to remove "orphaned" references after a row deletion.
 *
 * This function:
 * 1. Gets all relation values in the specified column
 * 2. Checks if each referenced row still exists
 * 3. Removes references to deleted rows
 * 4. Syncs the removal to the inverse side (cross-doc aware)
 */
export async function cleanupDeletedRowReferences(
  store: Store,
  sourceDatabaseId: string,
  sourcePropertyId: string,
  targetDatabaseId: string,
  targetDocId: string | undefined,
  inverseFieldId: string | undefined,
  inverseCardinality: 'one' | 'many'
): Promise<number> {
  const sourceDataSource = getDatabaseDataSource(store, sourceDatabaseId);
  if (!sourceDataSource) {
    return 0;
  }

  // Check if target exists (for cross-doc, we can't easily check)
  const isCrossDoc =
    targetDocId && targetDocId !== store.id && targetDocId.length > 0;

  let cleanedCount = 0;
  const rowIds = sourceDataSource.getLogicalRowIdsSnapshot();

  for (const rowId of rowIds) {
    const cellValue = sourceDataSource.cellValueGet(rowId, sourcePropertyId);
    const relationArray = normalizeToArray(cellValue);

    if (relationArray.length === 0) continue;

    // For local target DB, check which rows still exist
    let validRelations: string[] = relationArray;
    let removedRelations: string[] = [];

    if (!isCrossDoc) {
      const targetDataSource = getDatabaseDataSource(store, targetDatabaseId);
      if (targetDataSource) {
        validRelations = relationArray.filter(id =>
          targetDataSource.rowExists(id)
        );
        removedRelations = relationArray.filter(
          id => !targetDataSource.rowExists(id)
        );
      }
    }

    // If some relations were removed, update the cell and sync inverse
    if (removedRelations.length > 0) {
      const rollbackTasks: Array<() => Promise<void> | void> = [];

      // Sync removal to inverse side
      if (inverseFieldId) {
        const ctx: SyncContext = {
          store,
          sourceDatabaseId,
          sourcePropertyId,
          sourceRowId: rowId,
          targetDatabaseId,
          targetDocId,
          inverseFieldId,
          inverseCardinality,
        };

        try {
          for (const removedRowId of removedRelations) {
            const previousInverseValue = await syncInverseRelation(
              ctx,
              removedRowId,
              'remove'
            );
            rollbackTasks.push(() =>
              restoreInverseRelationValue(
                ctx,
                removedRowId,
                previousInverseValue
              )
            );
          }
        } catch (error) {
          await runRelationRollbackTasks(rollbackTasks);
          throw error;
        }
      }

      try {
        sourceDataSource.cellValueChange(
          rowId,
          sourcePropertyId,
          validRelations
        );
        cleanedCount += removedRelations.length;
      } catch (error) {
        await runRelationRollbackTasks(rollbackTasks);
        throw error;
      }
    }
  }

  if (cleanedCount > 0) {
    console.log('[cleanupDeletedRowReferences] Cleaned up:', {
      sourceDatabaseId,
      sourcePropertyId,
      cleanedCount,
    });
  }

  return cleanedCount;
}

async function runRelationRollbackTasks(
  rollbackTasks: Array<() => Promise<void> | void>
): Promise<void> {
  for (const rollback of rollbackTasks.reverse()) {
    try {
      await rollback();
    } catch (rollbackError) {
      console.error(
        '[sync] Failed to rollback relation cleanup:',
        rollbackError
      );
    }
  }
}

// === Helper Functions ===

function normalizeToArray(value: unknown): string[] {
  if (!value) return [];
  if (Array.isArray(value)) return value;
  return [String(value)];
}

function getRowDisplay(
  store: Store,
  databaseId: string,
  rowId: string
): string {
  const dataSource = getDatabaseDataSource(store, databaseId);
  if (!dataSource) {
    // Cross-doc: Database not in current store - just return truncated ID
    console.log(
      '[sync] getRowDisplay: Database',
      databaseId,
      'not in current store (cross-doc)'
    );
    return rowId.slice(0, 8);
  }

  // Try title first (special property)
  try {
    const titleValue = dataSource.cellValueGet(rowId, 'title');
    if (titleValue) {
      // Title is a Y.Text object, convert to string
      const text = titleValue.toString?.() ?? String(titleValue);
      if (text && text !== '[object Object]') {
        return text;
      }
    }

    // Fallback: try first text property through the bounded property window.
    const properties = dataSource.getBoundedPropertyIdsWindow().propertyIds;
    for (const propId of properties) {
      const propType = dataSource.propertyTypeGet(propId);
      if (propType === 'text' || propType === 'rich-text') {
        const value = dataSource.cellValueGet(rowId, propId);
        if (value && typeof value === 'string') {
          return value;
        }
      }
    }
  } catch (e) {
    console.warn('[sync] getRowDisplay error:', e);
  }

  return rowId.slice(0, 8);
}
